<script setup lang="ts">
import { computed, h, nextTick, ref, render, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { type FlattenedItem, TreeItem } from 'reka-ui'
import { unrefElement } from '@vueuse/core'
import bbox from '@turf/bbox'
import { useRouter, useRoute } from 'vue-router'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { type Instruction, type ItemMode, attachInstruction, extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item'

import {Eye, EyeClosed, Settings, ScanEye, Trash2, Globe, Package, Radar, Folders, Image, Ellipsis} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { LayerItemMapLayer } from '@/components/layers'
import { Spinner } from '@/components/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

const router = useRouter()
const route = useRoute()
const store = useStore()
const emit = defineEmits(['deleteLayer', 'select'])
const props = defineProps<{
  item: FlattenedItem<any>,
  prevItem?: FlattenedItem<any>,
}>()

type LayerType = 0 | 1 | 2 | 3 | 4 | 5

interface LayerTypeConfig {
  label: string
  icon: any
  color: string
}

const layerTypes: Record<LayerType, LayerTypeConfig> = {
  0: {
    label: 'PlanetScope',
    icon: Image,
    color: '#FF6B6B'
  },
  1: {
    label: 'Order Scene',
    icon: Package,
    color: '#4CAF50'
  },
  2: {
    label: 'Planetary Variables',
    icon: Globe,
    color: '#7ED321'
  },
  3: {
    label: 'Radar',
    icon: Radar,
    color: '#1A73E8'
  },
  4: {
    label: 'Collected Data',
    icon: Folders,
    color: '#FF9900'
  },
  5: {
    label: 'Static',
    icon: Folders,
    color: '#FF9900'
  }
}

const orgId = props.item.value.properties.organization_id
const layerId = `layer-${props.item.value.uuid}`
const beforeId = props.prevItem ? `layer-${props.prevItem.value.uuid}` : 'default-layer'

const map = computed(() => store.getters['geo/getMap'])
const map2 = computed(() => store.getters['geo/getMap2'])
const mapReady = computed(() => store.getters['geo/getMapReady'])
const map2Ready = computed(() => store.getters['geo/getMap2Ready'])

const isCompareMode = computed(() => store.getters['geo/getIsCompareMode'])

const elRef = ref()
const isDataLoaded = ref(false)
const isVisible = ref(props.item.value.properties?.visible ?? true)
const isHovered = ref(false)
const isDragging = ref(false)
const isDraggedOver = ref(false)
const isInitialExpanded = ref(false)
const instruction = ref<Extract<Instruction, { type: 'reorder-above' | 'reorder-below' | 'make-child' }> | null>(null)

const hasPlanetscopeAccess = computed(() => store.getters['geo/getIsPlanetscopeAccess'])

const layerType = computed(() => {
  const type = props.item.value.properties?.type as LayerType
  return layerTypes[type] || layerTypes[0] // Default to PlanetScope if type is not found
})

const toggleVisibility = () => {
  isVisible.value = !isVisible.value

  const layers = map.value.getStyle().layers.filter((layer: any) =>
    layer.metadata?.groupId === layerId
  )

  layers.forEach((layer: any) => {
    map.value.setLayoutProperty(
      layer.id,
      'visibility',
      isVisible.value ? 'visible' : 'none'
    )
  })

  // Update the layer in Vuex store
  const updatedLayer = {
    ...props.item.value,
    properties: {
      ...props.item.value.properties,
      visible: isVisible.value
    }
  }

  store.dispatch('layers/updateOrgLayer', {
    orgId,
    layer: updatedLayer
  })
}

const showDeleteConfirm = ref(false)

const deleteLayer = async () => {
  // Add confirmation to prevent accidental deletion
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  try {
    console.log('Deleting layer:', props.item.value.uuid, props.item.value.properties?.name)
    await store.dispatch('layers/deleteOrgLayer', {
      orgId,
      layerId: props.item.value.uuid
    })
    emit('deleteLayer', props.item.value.uuid)
    showDeleteConfirm.value = false
  } catch (error) {
    console.error('Error deleting layer:', error)
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
}

const focusOnGeometry = () => {
  const geometry = props.item.value?.geometry
  if (geometry) {
    const bounds = bbox(geometry)
    map.value.fitBounds(bounds, { padding: 300 })
  }
}

const mode = computed(() => {
  if (props.item.hasChildren)
    return 'expanded'
  if (props.item.index + 1 === props.item.parentItem?.children?.length)
    return 'last-in-group'
  return 'standard'
})

const navigateToLayerDetail = () => {
  router.push({
    name: 'LayerDetail',
    params: {
      org_id: orgId,
      layer_id: props.item.value.uuid
    }
  })
}

watchEffect((onCleanup) => {
  const currentElement = unrefElement(elRef)

  if (!currentElement)
    return

  const item = { ...props.item.value, level: props.item.level, id: props.item._id }

  const expandItem = () => {
    if (!elRef.value?.isExpanded) {
      elRef.value?.handleToggle()
    }
  }

  const closeItem = () => {
    if (elRef.value?.isExpanded) {
      elRef.value?.handleToggle()
    }
  }

  const dndFunction = combine(
    draggable({
      element: currentElement,
      getInitialData: () => item,
      onDragStart: () => {
        isDragging.value = true
        isInitialExpanded.value = elRef.value?.isExpanded
        closeItem()
      },
      onDrop: () => {
        isDragging.value = false
        if (isInitialExpanded.value)
          expandItem()
      },
      onGenerateDragPreview({ nativeSetDragImage }) {
        setCustomNativeDragPreview({
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
          render: ({ container }) => {
            return render(h(
              'div',
              { class: 'bg-white text-blackA11 rounded-md text-sm font-medium px-3 py-1.5' },
              item.name,
            ), container)
          },
          nativeSetDragImage,
        })
      },
    }),

    dropTargetForElements({
      element: currentElement,
      getData: ({ input, element }) => {
        const data = { id: item.id }

        return attachInstruction(data, {
          input,
          element,
          indentPerLevel: 16,
          currentLevel: props.item.level,
          mode: mode.value,
          block: [],
        })
      },
      canDrop: ({ source }) => {
        return source.data.id !== item.id
      },
      onDrag: ({ self }) => {
        instruction.value = extractInstruction(self.data) as typeof instruction.value
      },
      onDragEnter: ({ source }) => {
        if (source.data.id !== item.id) {
          isDraggedOver.value = true
          expandItem()
        }
      },
      onDragLeave: () => {
        isDraggedOver.value = false
        instruction.value = null
      },
      onDrop: ({ location }) => {
        isDraggedOver.value = false
        instruction.value = null
        if (location.current.dropTargets[0].data.id === item.id) {
          nextTick(() => {
            expandItem()
          })
        }
      },
      getIsSticky: () => true,
    }),

    monitorForElements({
      canMonitor: ({ source }) => {
        return source.data.id !== item.id
      },
    }),
  )

  onCleanup(() => dndFunction())
})
</script>

<template>
  <div>
    <TreeItem
      ref="elRef"
      v-slot="{ isExpanded }"
      :value="item.value"
      :level="item.level"
      class="relative w-full px-3 py-1 rounded-none cursor-pointer"
      :class="{ 'opacity-50': isDragging, 'bg-primary/10': route.params.layer_id === item.value.uuid }"
      @click="navigateToLayerDetail"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
    <div class="flex justify-between items-center w-full">
      <div class="flex items-center">
        <!-- <Button
          variant="ghostDark"
          class="w-7 h-7 mr-0.5 p-0 text-gray-400 rounded-full hover:text-black"
          :class="{'rotate-90 text-black': isExpanded}"
          @click="toggleExpand"
        >
          <ChevronRight class="w-7 h-5" />
        </Button> -->
        <div class=" flex items-center">
          <component :is="layerType.icon" class="w-4.5 h-4.5 mr-1" :style="{ color: layerType.color }" />
          <h3>{{ item.value.properties.name?.trim() || 'Unnamed' }}</h3>
        </div>
      </div>
      <div class="opacity-0" :class="{'opacity-50 pointer-events-none': !isDataLoaded, 'opacity-100': isHovered}">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full">
              <Ellipsis class="w-4 h-4 text-black" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-48">
            <DropdownMenuGroup>
              <DropdownMenuItem v-if="item.value.geometry" @click="focusOnGeometry" class="cursor-pointer">
                <ScanEye class="w-4 h-4 mr-2" />
                <span>Fly To</span>
              </DropdownMenuItem>
              <DropdownMenuItem @click="deleteLayer" class="text-red-500 cursor-pointer">
                <Trash2 class="w-4 h-4 mr-2" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <TooltipProvider :delayDuration="450">
          <Tooltip>
            <TooltipTrigger>
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click="toggleVisibility">
                <EyeClosed class="text-black w-4 h-4" v-if="!isVisible" />
                <Eye class="text-primary w-4 h-4" v-if="isVisible" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Visibility</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>

    <div v-if="!isDataLoaded" class="p-2 flex items-center">
      <Spinner class="w-5 h-5 mr-2" />
      <p>Loading Data</p>
    </div>

    <div v-if="map2 && map2Ready && isCompareMode">
      <div class="mx-2">
        <span>Top</span>
      </div>
      <LayerItemMapLayer
        :map="map2"
        :item="item.value"
        :layerId="layerId + '-map2'"
        :beforeId="beforeId + '-map2'"
        :orgId="orgId"
        :isVisible="isVisible"
        @isLoaded="isDataLoaded = $event"
        @deleteLayer="deleteLayer"
      ></LayerItemMapLayer>
    </div>

    <div v-if="mapReady">
      <div class="mx-2">
        <span v-if="map2 && map2Ready && isCompareMode">Bottom</span>
      </div>
      <LayerItemMapLayer
        v-if="mapReady"
        :map="map"
        :item="item.value"
        :layerId="layerId"
        :beforeId="beforeId"
        :orgId="orgId"
        :isVisible="isVisible"
        @isLoaded="isDataLoaded = $event"
        @deleteLayer="deleteLayer"
      ></LayerItemMapLayer>
    </div>


    <div
      v-if="instruction"
      class="absolute cursor-auto h-full w-full top-0 border-blue-500 "
      :style="{
        left: `${instruction?.currentLevel * instruction?.indentPerLevel}px`,
        width: `calc(100% - ${instruction?.currentLevel * instruction?.indentPerLevel}px)`,
      }"
      :class="{
        'border-b-2!': instruction?.type === 'reorder-below',
        'border-t-2!': instruction?.type === 'reorder-above',
        'border-2! rounded': instruction?.type === 'make-child',
    }"
    />
    </TreeItem>
    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="showDeleteConfirm">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Layer</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the layer "{{ props.item.value.properties?.name }}"?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="cancelDelete">Cancel</Button>
          <Button variant="destructive" @click="confirmDelete">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
